import { useState, useMemo, useRef, useEffect } from 'react';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { LedgerEntry, CustomerDeposit } from '../../types';
import { allocationsApi, ledgerApi, depositsApi } from '../../api';
import UnallocatedLedgerPanel from './UnallocatedLedgerPanel';
import UnallocatedDepositsPanel from './UnallocatedDepositsPanel';
import { useAccountPreference, useAccountMenuItem } from '../../hooks';

interface ManualDepositMatchTabProps {
  clientId: string;
}

export const ManualDepositMatchTab: React.FC<ManualDepositMatchTabProps> = ({ clientId }) => {
  const [selectedLedgerEntryId, setSelectedLedgerEntryId] = useState<string | null>(null);
  const [selectedDepositIds, setSelectedDepositIds] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [depositAmountFilter, setDepositAmountFilter] = useState<string | null>(null);
  const toast = useRef<Toast | null>(null);
  const { selectedAccountId } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId);

  // Data loading state
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [deposits, setDeposits] = useState<CustomerDeposit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [clientId, selectedAccountId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [ledgerData, depositsData] = await Promise.all([
        ledgerApi.listLedgerEntries(clientId, { unallocatedOnly: true, accountId: selectedAccountId, entryType: 'deposit' }),
        depositsApi.listDeposits(clientId, { accountId: selectedAccountId }),
      ]);

      // Backend already filters to unallocated customer entries
      setLedgerEntries(ledgerData);
      // Unallocated deposits: have a customer, are approved, and not yet allocated
      setDeposits(depositsData.filter((d) =>
        d.customer_id &&
        !d.customer_allocation_id &&
        d.status === 'approved'
      ));
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate min and max dates from data
  const { minDate, maxDate } = useMemo(() => {
    const parseDates = (items: Array<{ entry_date?: string | null; deposit_date?: string | null }>) => {
      return items
        .map((item) => {
          const dateStr = (item as LedgerEntry).entry_date || (item as CustomerDeposit).deposit_date;
          return dateStr ? new Date(dateStr) : null;
        })
        .filter((d): d is Date => d !== null && !isNaN(d.getTime()));
    };

    const ledgerDates = parseDates(ledgerEntries);
    const depositDates = parseDates(deposits);

    const ledgerMin = ledgerDates.length > 0 ? new Date(Math.min(...ledgerDates.map((d) => d.getTime()))) : null;
    const ledgerMax = ledgerDates.length > 0 ? new Date(Math.max(...ledgerDates.map((d) => d.getTime()))) : null;
    const depositMin = depositDates.length > 0 ? new Date(Math.min(...depositDates.map((d) => d.getTime()))) : null;
    const depositMax = depositDates.length > 0 ? new Date(Math.max(...depositDates.map((d) => d.getTime()))) : null;

    // minDate: later of the two earliest dates
    const min = !ledgerMin ? depositMin : !depositMin ? ledgerMin : new Date(Math.max(ledgerMin.getTime(), depositMin.getTime()));

    // maxDate: earlier of the two latest dates
    const max = !ledgerMax ? depositMax : !depositMax ? ledgerMax : new Date(Math.min(ledgerMax.getTime(), depositMax.getTime()));

    return { minDate: min, maxDate: max };
  }, [ledgerEntries, deposits]);

  // Filter data by date range
  const filteredLedger = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
      return ledgerEntries;
    }

    return ledgerEntries.filter((entry) => {
      if (!entry.entry_date) return true;
      const entryDate = new Date(entry.entry_date);
      return entryDate >= dateRange[0] && entryDate <= dateRange[1];
    });
  }, [ledgerEntries, dateRange]);

  const filteredDeposits = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
      return deposits;
    }

    return deposits.filter((deposit) => {
      if (!deposit.deposit_date) return true;
      const depositDate = new Date(deposit.deposit_date);
      return depositDate >= dateRange[0] && depositDate <= dateRange[1];
    });
  }, [deposits, dateRange]);

  const selectedLedgerEntry = filteredLedger.find((e) => e.id === selectedLedgerEntryId);
  const selectedDeposits = filteredDeposits.filter(
    (d) => d.id && selectedDepositIds.has(d.id)
  );

  const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '$0.00';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numeric);
  };

  const getTotalDeposits = () => {
    return selectedDeposits.reduce((sum, d) => {
      if (d.amount === null || d.amount === undefined) return sum;
      return sum + Math.abs(d.amount);
    }, 0);
  };

  const getLedgerAmount = () => {
    if (!selectedLedgerEntry) return 0;
    return Math.abs(selectedLedgerEntry.amount);
  };

  const handleLinkClick = () => {
    const totalDeposits = getTotalDeposits();
    const ledgerAmount = getLedgerAmount();

    if (totalDeposits > ledgerAmount) {
      toast.current?.show({
        severity: 'error',
        summary: 'Linking blocked',
        detail: `Total deposits (${formatCurrency(totalDeposits)}) exceed ledger entry amount (${formatCurrency(ledgerAmount)})`,
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (!selectedLedgerEntryId || selectedDeposits.length === 0) return;

    setIsSaving(true);

    try {
      // Create manual allocation for each selected deposit
      for (const deposit of selectedDeposits) {
        await allocationsApi.createManualAllocation({
          client_id: clientId,
          customer_id: deposit.customer_id!,
          ledger_entry_id: selectedLedgerEntryId,
          deposit_id: deposit.id,
          amount: deposit.amount || 0,
          notes: notes || undefined,
        });
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Manual match created',
        detail: `Successfully linked ${selectedDeposits.length} deposit(s) to ledger entry`,
        life: 4000,
      });

      // Reset state
      setSelectedLedgerEntryId(null);
      setSelectedDepositIds(new Set());
      setDepositAmountFilter(null);
      setNotes('');
      setShowConfirmDialog(false);

      // Refresh data
      loadData();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Manual match failed',
        detail: err?.response?.data?.detail || err?.message || 'Failed to create manual match',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const totalDeposits = getTotalDeposits();
  const ledgerAmount = getLedgerAmount();
  const remaining = ledgerAmount - totalDeposits;

  const menuItems = [
    accountMenuItem,
    {
      label: 'Link',
      icon: 'ms-out ms-out-link',
      command: handleLinkClick,
      disabled: !selectedLedgerEntryId || selectedDepositIds.size === 0,
    },
    ...(selectedLedgerEntryId ? [{
      template: () => (
        <div className="menu-item-info">
          <span>Ledger: <strong>{formatCurrency(ledgerAmount)}</strong></span>
          {selectedDepositIds.size > 0 && (
            <>
              <span> - </span>
              <span>{selectedDepositIds.size} Deposit(s): <strong>{formatCurrency(totalDeposits)}</strong></span>
              <span> = </span>
              <span>Remaining: <strong>{formatCurrency(remaining)}</strong></span>
            </>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <>
      <Toast ref={toast} />

      <Menubar
        className="actions-menubar"
        model={menuItems}
        end={
          <div className="controls-group">
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value as Date[] | null)}
              selectionMode="range"
              minDate={minDate || undefined}
              maxDate={maxDate || undefined}
              placeholder="Filter by date range"
              showIcon
              numberOfMonths={3}
              dateFormat="yy-mm-dd"
              readOnlyInput
            />
            {dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1] && (
              <Button
                icon="ms-out ms-out-close"
                onClick={() => setDateRange(null)}
                size="small"
                severity="secondary"
                tooltip="Clear date filter"
              />
            )}
          </div>
        }
      />

      <Splitter>
        <SplitterPanel size={50} minSize={30}>
          <UnallocatedLedgerPanel
            entries={filteredLedger}
            loading={isLoading}
            selectionMode="single"
            selectedEntryId={selectedLedgerEntryId}
            onSelectionChange={setSelectedLedgerEntryId}
            onFilterByAmount={(amount) => setDepositAmountFilter(amount.toString())}
            title="Ledger Deposits"
          />
        </SplitterPanel>
        <SplitterPanel size={50} minSize={30}>
          <UnallocatedDepositsPanel
            clientId={clientId}
            deposits={filteredDeposits}
            loading={isLoading}
            selectionMode="multiple"
            selectedDepositIds={selectedDepositIds}
            onSelectionChange={setSelectedDepositIds}
            selectedLedgerEntryDate={selectedLedgerEntry?.entry_date || null}
            externalAmountFilter={depositAmountFilter}
          />
        </SplitterPanel>
      </Splitter>

      <Dialog
        header="Confirm Manual Match"
        visible={showConfirmDialog}
        className="manual-match-dialog"
        onHide={() => setShowConfirmDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isSaving}
            />
            <Button
              label="Confirm Link"
              className="p-button-success"
              onClick={handleConfirm}
              loading={isSaving}
              disabled={isSaving}
            />
          </div>
        }
      >
        <>
          <table className="details-table">
            <tbody>
              <tr>
                <td>Total Deposits</td>
                <td>{formatCurrency(totalDeposits)}</td>
              </tr>
              <tr>
                <td>Ledger Entry Amount</td>
                <td>{formatCurrency(ledgerAmount)}</td>
              </tr>
              <tr>
                <td>Remaining Unallocated</td>
                <td>{formatCurrency(remaining)}</td>
              </tr>
            </tbody>
          </table>

          <div className="form-item">
            <label htmlFor="notes">
              <strong>Notes (optional):</strong>
            </label>
            <InputTextarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="full-width-input"
              placeholder="Add any notes about this manual match..."
            />
          </div>
        </>
      </Dialog>
    </>
  );
};
